import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  ArrowBackRounded,
  PersonRounded,
  SchoolRounded,
  CalendarMonthRounded,
  SendRounded,
  ManageSearchRounded,
  ThumbUpAltRounded,
  ThumbDownAltRounded,
  HourglassTopRounded,
  MarkEmailUnreadRounded,
  EmailRounded,
  LinkRounded,
  RefreshRounded,
  OpenInNewRounded,
  ArticleRounded,
  UndoRounded,
  PersonOffRounded,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdmissionDetail, actionLabel } from './hooks/use-admission-detail';
import { AdmissionDocumentsReview } from './components/AdmissionDocumentsReview';
import { useTenantStore } from '../../stores/tenant.store';
import { ReviewPanel } from './components/ReviewPanel';
import { WithdrawStudentDialog } from './components/WithdrawStudentDialog';
import type { SxProps, Theme } from '@mui/material';
import { errorReason, errorText } from '../../lib/error-text';
import { formatDate, formatTime } from '../../lib/i18n-format';
import { callable } from '../../lib/callable';

// ─── Status Config ──────────────────────────────────────────────────────────

type ChipColor = 'default' | 'info' | 'warning' | 'success' | 'error';
type ActionColor = 'primary' | 'success' | 'error' | 'warning';

interface StatusMeta {
  label: string;
  color: ChipColor;
  stepIndex: number;
}

const STATUS_META: Record<string, StatusMeta> = {
  draft: { label: 'Borrador', color: 'default', stepIndex: 0 },
  awaiting_applicant: { label: 'Esperando Postulante', color: 'info', stepIndex: 1 },
  submitted: { label: 'Enviada', color: 'info', stepIndex: 2 },
  under_review: { label: 'En Revisión', color: 'warning', stepIndex: 3 },
  waitlisted: { label: 'Lista Espera', color: 'warning', stepIndex: 3 },
  accepted: { label: 'Aceptada', color: 'success', stepIndex: 4 },
  rejected: { label: 'Rechazada', color: 'error', stepIndex: 4 },
};

const STEPS = ['Borrador', 'Invitado', 'Formulario', 'En Revisión', 'Decisión'];

// ─── Info Card ──────────────────────────────────────────────────────────────

function InfoCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        border: '1px solid rgba(255,255,255,0.07)',
        background: 'rgba(255,255,255,0.02)',
      }}
    >
      <Box sx={{ color: 'primary.main', mt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
        <Typography variant="body1" fontWeight={600} color="text.primary">
          {value ?? '—'}
        </Typography>
      </Box>
    </Paper>
  );
}

// ─── Awaiting Applicant Banner ───────────────────────────────────────────────

interface AwaitingBannerProps {
  invitedAt?: Date;
  personEmail?: string;
  onResend: () => Promise<void>;
  resending: boolean;
}

function AwaitingBanner({ invitedAt, personEmail, onResend, resending }: AwaitingBannerProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid rgba(99,102,241,0.25)',
        background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.05) 100%)',
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <MarkEmailUnreadRounded sx={{ color: 'primary.main', fontSize: 28, flexShrink: 0 }} />
      <Box flex={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          Invitación Enviada — Esperando al Postulante
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {personEmail
            ? `Se envió el link a ${personEmail}`
            : 'El link de acceso fue enviado por email'}
          {invitedAt && ` el ${formatDate(invitedAt)}`}
        </Typography>
      </Box>
      <Tooltip title="Reenviar invitación">
        <Button
          size="small"
          variant="outlined"
          startIcon={resending ? <CircularProgress size={14} /> : <RefreshRounded />}
          onClick={onResend}
          disabled={resending}
          sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
        >
          Reenviar
        </Button>
      </Tooltip>
    </Paper>
  );
}

// ─── Accepted Banner (resend welcome email) ──────────────────────────────────

interface AcceptedBannerProps {
  personEmail?: string;
  hasAccount: boolean;
  onResend: () => Promise<void>;
  resending: boolean;
  resendSuccess: boolean;
  resendError?: string | null;
}

function AcceptedBanner({
  personEmail,
  hasAccount,
  onResend,
  resending,
  resendSuccess,
  resendError,
}: AcceptedBannerProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid rgba(16,185,129,0.25)',
        background: 'linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(5,150,105,0.04) 100%)',
      }}
    >
      {/* Main row */}
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <EmailRounded sx={{ color: 'success.main', fontSize: 28, flexShrink: 0 }} />
        <Box flex={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            {hasAccount
              ? 'Cuenta en proceso de activación'
              : 'Correo de bienvenida pendiente de envío'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {personEmail
              ? `El alumno aún no ha creado su contraseña (${personEmail}).`
              : 'El alumno aún no ha activado su acceso al portal.'}{' '}
            Puedes reenviar el correo de bienvenida con el link de activación.
          </Typography>
        </Box>
        <Tooltip title="Reenviar correo de bienvenida">
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={
              resending ? <CircularProgress size={14} color="inherit" /> : <RefreshRounded />
            }
            onClick={onResend}
            disabled={resending}
            sx={{
              borderRadius: 2,
              whiteSpace: 'nowrap',
              borderColor: 'rgba(16,185,129,0.4)',
              flexShrink: 0,
            }}
          >
            {resending ? 'Enviando…' : 'Reenviar bienvenida'}
          </Button>
        </Tooltip>
      </Box>
      {/* Feedback */}
      {resendSuccess && (
        <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
          Correo de bienvenida reenviado a {personEmail ?? 'el alumno'}.
        </Alert>
      )}
      {resendError && (
        <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
          {resendError}
        </Alert>
      )}
    </Paper>
  );
}

// ─── Action Config ──────────────────────────────────────────────────────────

interface ActionDef {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: ActionColor;
  fn: string;
  requiresNotes: boolean;
}

const ACTIONS: Record<string, ActionDef> = {
  invite: {
    label: 'Invitar Postulante',
    description:
      'Enviar un link de acceso al email del postulante para que complete su formulario de admisión.',
    icon: <LinkRounded />,
    color: 'primary',
    fn: 'inviteApplicant',
    requiresNotes: false,
  },
  submit: {
    label: 'Enviar a Revisión',
    description:
      'Enviar la solicitud directamente a revisión por el comité (sin formulario del postulante).',
    icon: <SendRounded />,
    color: 'primary',
    fn: 'submitAdmission',
    requiresNotes: false,
  },
  startReview: {
    label: 'Iniciar Revisión',
    description: 'Marcar que el comité comenzó la evaluación de esta solicitud.',
    icon: <ManageSearchRounded />,
    color: 'warning',
    fn: 'startAdmissionReview',
    requiresNotes: false,
  },
  accept: {
    label: 'Aceptar Postulante',
    description: 'El postulante será aceptado y avanzará a Estudiante Activo.',
    icon: <ThumbUpAltRounded />,
    color: 'success',
    fn: 'acceptAdmission',
    requiresNotes: true,
  },
  reject: {
    label: 'Rechazar Postulante',
    description: 'El postulante será rechazado y su estado volverá a Externo.',
    icon: <ThumbDownAltRounded />,
    color: 'error',
    fn: 'rejectAdmission',
    requiresNotes: true,
  },
  waitlist: {
    label: 'Lista de Espera',
    description: 'Colocar la solicitud en lista de espera pendiente de cupo.',
    icon: <HourglassTopRounded />,
    color: 'warning',
    fn: 'waitlistAdmission',
    requiresNotes: true,
  },
  resetToDraft: {
    label: 'Revertir a Borrador',
    description:
      'Reiniciar la solicitud a estado Borrador para volver a iniciar el proceso de revisión. Esta acción es solo para administradores.',
    icon: <UndoRounded />,
    color: 'warning',
    fn: 'resetAdmissionToDraft',
    requiresNotes: false,
  },
};

const ACTIONS_BY_STATUS: Record<string, string[]> = {
  // draft: admin can invite the applicant OR manually bypass the form step
  draft: ['invite', 'submit'],
  // awaiting_applicant: no manual staff action — waiting for the applicant
  awaiting_applicant: [],
  // submitted: form received — staff must formally start the review first
  submitted: ['startReview'],
  // Quorum engine handles auto-decisions, but admin can override as an exception.
  under_review: ['accept', 'reject', 'waitlist', 'resetToDraft'],
  waitlisted: ['accept', 'reject', 'resetToDraft'],
};

// ─── Action Panel ───────────────────────────────────────────────────────────

interface ActionPanelProps {
  status: string;
  admissionId: string;
  onAction: (action: string, notes?: string) => Promise<void>;
  actionLoading: boolean;
  actionError: string | null;
  isAdmin: boolean;
}

// Actions that require admin role (tenantAdmin / registrar) regardless of status
const ADMIN_OVERRIDE_ACTIONS = new Set(['accept', 'reject', 'waitlist', 'resetToDraft']);

function ActionPanel({ status, onAction, actionLoading, actionError, isAdmin }: ActionPanelProps) {
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const rawKeys = ACTIONS_BY_STATUS[status] ?? [];
  // Filter out admin-only actions for non-admin users
  const availableKeys = rawKeys.filter((k) => !ADMIN_OVERRIDE_ACTIONS.has(k) || isAdmin);
  if (availableKeys.length === 0) return null;

  const isDecision = (key: string) => ['accept', 'reject', 'waitlist'].includes(key);
  const selectedDef: ActionDef | undefined = confirmKey ? ACTIONS[confirmKey] : undefined;

  const gradientSx = (color: ActionColor): SxProps<Theme> => {
    if (color === 'success')
      return { background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' };
    if (color === 'error')
      return { background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)' };
    return {};
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid rgba(99,102,241,0.2)',
          background:
            'linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.03) 100%)',
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          mb={2}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Acciones Disponibles
        </Typography>
        {actionError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {actionError}
          </Alert>
        )}
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          {availableKeys.map((key) => {
            const act = ACTIONS[key];
            if (!act) return null;
            return (
              <Button
                key={key}
                variant={isDecision(key) ? 'contained' : 'outlined'}
                color={act.color}
                startIcon={act.icon}
                onClick={() => setConfirmKey(key)}
                disabled={actionLoading}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  ...(isDecision(key) ? gradientSx(act.color) : {}),
                }}
              >
                {act.label}
              </Button>
            );
          })}
          {actionLoading && (
            <CircularProgress size={20} sx={{ alignSelf: 'center', color: 'primary.main' }} />
          )}
        </Stack>
      </Paper>

      {/* Confirm Dialog */}
      <Dialog
        open={!!confirmKey}
        onClose={() => {
          setConfirmKey(null);
          setNotes('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, border: '1px solid rgba(255,255,255,0.08)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {selectedDef ? (
            <>
              <Box
                component="span"
                sx={{ mr: 1, verticalAlign: 'middle', color: `${selectedDef.color}.main` }}
              >
                {selectedDef.icon}
              </Box>
              {selectedDef.label}
            </>
          ) : null}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: selectedDef?.requiresNotes ? 2 : 0 }}>
            {selectedDef?.description}
          </DialogContentText>
          {selectedDef?.requiresNotes && (
            <TextField
              label="Notas de decisión (opcional)"
              multiline
              minRows={3}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej. Excelente perfil pastoral, carta de referencia positiva..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
          <Button
            onClick={() => {
              setConfirmKey(null);
              setNotes('');
            }}
            disabled={actionLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={selectedDef?.color ?? 'primary'}
            disabled={actionLoading}
            onClick={async () => {
              if (!confirmKey) return;
              await onAction(confirmKey, notes || undefined);
              setConfirmKey(null);
              setNotes('');
            }}
            sx={{ borderRadius: 2 }}
          >
            {actionLoading ? <CircularProgress size={18} color="inherit" /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function AdmissionDetailPage() {
  const { t } = useTranslation('sis');
  const { t: tCommon } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentTenantId, currentMembership } = useTenantStore();
  const currentPersonId = currentMembership?.personId;
  const isAdmin = (currentMembership?.roles ?? []).some(
    (r) => r === 'tenantAdmin' || r === 'registrar',
  );
  const { detail, historyEvents, loading, error } = useAdmissionDetail(id);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [docBusy, setDocBusy] = useState(false);
  const [docOverride, setDocOverride] = useState<{ notes: string; message: string } | null>(null);
  // Ladder entry rule: the student has not completed the program this one builds
  // on. The registrar may accept anyway, with a reason that is audited.
  const [stackOverride, setStackOverride] = useState<{
    notes: string;
    message: string;
    overrideDocuments: boolean;
    reason: string;
  } | null>(null);
  // Accepted, but the program builds on one the student has not completed and
  // does not enforce the rule — the registrar should still know.
  const [stackWarning, setStackWarning] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState(false);
  const [resendWelcomeSuccess, setResendWelcomeSuccess] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const callFn = async (fnName: string, payload: Record<string, unknown>) => {
    const fn = callable(fnName);
    return (await fn(payload)).data as Record<string, unknown> | undefined;
  };

  const handleAction = async (
    action: string,
    notes?: string,
    overrideDocuments?: boolean,
    stackEntryOverrideReason?: string,
  ) => {
    if (!currentTenantId || !id) return;
    setActionLoading(true);
    setActionError(null);
    setDocOverride(null);
    setStackOverride(null);
    setStackWarning(null);
    try {
      const fnName = ACTIONS[action]?.fn ?? action;
      const result = await callFn(fnName, {
        tenantId: currentTenantId,
        admissionId: id,
        ...(notes ? { decisionNotes: notes } : {}),
        ...(overrideDocuments ? { overrideDocuments: true } : {}),
        ...(stackEntryOverrideReason
          ? { stackEntryOverride: { reason: stackEntryOverrideReason } }
          : {}),
      });
      const warning = result?.['stackEntryWarning'] as
        | { predecessorProgramName: string }
        | undefined;
      if (action === 'accept' && warning) {
        setStackWarning(
          t('admissionDetail.stackEntry.warning', { program: warning.predecessorProgramName }),
        );
      }
      // Accepted, but the program enrollment could not be created: without it the
      // student has no progress audit and no catalog. It is audited server-side;
      // the registrar has to know now, not discover it later.
      if (action === 'accept' && result?.['programEnrollmentFailed']) {
        setStackWarning(t('admissionDetail.programEnrollmentFailed'));
      }
    } catch (err: unknown) {
      const message = errorText(err, t('admissionDetail.errors.actionFailed'));
      if (action === 'accept' && errorReason(err) === 'program_enrollment.predecessor_incomplete') {
        setStackOverride({
          notes: notes ?? '',
          message,
          overrideDocuments: !!overrideDocuments,
          reason: '',
        });
      } else if (
        action === 'accept' &&
        (errorReason(err) === 'admission.documents_unverified' || /document/i.test(message))
      ) {
        // Missing required documents on accept → offer an explicit override.
        setDocOverride({ notes: notes ?? '', message });
      } else {
        setActionError(message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const verifyDoc = async (type: string) => {
    if (!currentTenantId || !id) return;
    setDocBusy(true);
    try {
      await callFn('verifyAdmissionDocument', {
        tenantId: currentTenantId,
        admissionId: id,
        documentType: type,
      });
    } finally {
      setDocBusy(false);
    }
  };

  const rejectDoc = async (type: string, reason: string) => {
    if (!currentTenantId || !id) return;
    setDocBusy(true);
    try {
      await callFn('rejectAdmissionDocument', {
        tenantId: currentTenantId,
        admissionId: id,
        documentType: type,
        reason,
      });
    } finally {
      setDocBusy(false);
    }
  };

  const handleResend = async () => {
    if (!currentTenantId || !id) return;
    setResending(true);
    setActionError(null);
    try {
      await callFn('inviteApplicant', {
        tenantId: currentTenantId,
        admissionId: id,
      });
    } catch (err: unknown) {
      const message = errorText(err, t('admissionDetail.errors.resendInviteFailed'));
      console.error('[AdmissionDetail] resend error', err);
      setActionError(message);
    } finally {
      setResending(false);
    }
  };

  const handleResendWelcome = async () => {
    if (!currentTenantId || !detail?.personId) return;
    setResendingWelcome(true);
    setResendWelcomeSuccess(false);
    setActionError(null);
    try {
      await callFn('resendStudentWelcomeEmail', {
        tenantId: currentTenantId,
        personId: detail.personId,
      });
      setResendWelcomeSuccess(true);
      setTimeout(() => setResendWelcomeSuccess(false), 5000);
    } catch (err: unknown) {
      const message = errorText(err, 'Error al reenviar el correo de bienvenida');
      console.error('[AdmissionDetail] resendWelcome error', err);
      setActionError(message);
    } finally {
      setResendingWelcome(false);
    }
  };

  // Safety fallback for existing DB records where the applicant completed the form
  // but the status was incorrectly left as 'draft' (pre-domain-fix records).
  // Treat them as 'submitted' everywhere: chip, stepper, and action panel.
  const effectiveStatus =
    detail?.status === 'draft' &&
    detail.applicantFormData &&
    Object.keys(detail.applicantFormData).length > 0
      ? 'submitted'
      : (detail?.status ?? 'draft');

  const statusMeta: StatusMeta = STATUS_META[effectiveStatus] ?? STATUS_META['draft']!;
  const isFinished = ['accepted', 'rejected'].includes(effectiveStatus);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate('/admissions')}
          sx={{ mb: 2 }}
        >
          Volver a Admisiones
        </Button>
        <Alert severity="error">{error ?? 'No se pudo cargar la admisión'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
      {/* ── Header ── */}
      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <Tooltip title="Volver a Admisiones">
          <IconButton
            onClick={() => navigate('/admissions')}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <ArrowBackRounded />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" color="text.disabled">
          Admisiones /
        </Typography>
        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
          {id?.slice(0, 12)}…
        </Typography>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={4}
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} color="white">
            {detail.personName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {detail.programName} · {detail.termName}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={1.5}>
          {detail.status === 'accepted' && isAdmin && (
            <Tooltip title="Registrar retiro del alumno">
              <Button
                id="withdraw-student-btn"
                variant="outlined"
                color="error"
                size="small"
                startIcon={<PersonOffRounded />}
                onClick={() => setWithdrawOpen(true)}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Registrar Retiro
              </Button>
            </Tooltip>
          )}
          <Chip
            label={statusMeta.label}
            color={statusMeta.color}
            size="medium"
            sx={{ fontWeight: 700, fontSize: '0.85rem', px: 1 }}
          />
        </Stack>
      </Stack>

      {/* ── Progress Stepper ── */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          mb: 4,
          borderRadius: 3,
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <Stepper
          activeStep={isFinished ? statusMeta.stepIndex + 1 : statusMeta.stepIndex}
          alternativeLabel
        >
          {STEPS.map((labelText, index) => {
            const isCompleted = isFinished
              ? index <= statusMeta.stepIndex
              : index < statusMeta.stepIndex;

            const isDecisionStep = labelText === 'Decisión';
            const decisionLabel =
              isDecisionStep && isFinished
                ? detail.status === 'accepted'
                  ? '✅ Aceptada'
                  : '❌ Rechazada'
                : labelText;

            return (
              <Step key={labelText} completed={isCompleted}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      color:
                        index === statusMeta.stepIndex && !isFinished
                          ? 'primary.main'
                          : 'text.secondary',
                      fontWeight: index === statusMeta.stepIndex ? 700 : 400,
                    },
                  }}
                >
                  {decisionLabel}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* ── Awaiting Applicant Banner ── */}
      {detail.status === 'awaiting_applicant' && (
        <Box mb={4}>
          <AwaitingBanner
            {...(detail.invitedAt && { invitedAt: detail.invitedAt })}
            {...(detail.personEmail && { personEmail: detail.personEmail })}
            onResend={handleResend}
            resending={resending}
          />
        </Box>
      )}

      {/* ── Accepted Banner (resend welcome email) ── */}
      {detail.status === 'accepted' && isAdmin && (
        <Box mb={4}>
          <AcceptedBanner
            {...(detail.personEmail && { personEmail: detail.personEmail })}
            hasAccount={!!detail.personAuthUid}
            onResend={handleResendWelcome}
            resending={resendingWelcome}
            resendSuccess={resendWelcomeSuccess}
            resendError={actionError}
          />
        </Box>
      )}

      {/* ── Applicant Form Answers (after completion) ── */}
      {detail.applicantFormData && Object.keys(detail.applicantFormData).length > 0 && (
        <Box mb={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid rgba(16,185,129,0.25)',
              background:
                'linear-gradient(135deg, rgba(16,185,129,0.05) 0%, rgba(5,150,105,0.03) 100%)',
              cursor: 'pointer',
              transition: 'all 0.18s ease',
              '&:hover': {
                border: '1px solid rgba(16,185,129,0.5)',
                background:
                  'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(5,150,105,0.06) 100%)',
                transform: 'translateY(-1px)',
              },
            }}
            onClick={() => window.open(`/admissions/${id}/application`, '_blank')}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: 'rgba(16,185,129,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ArticleRounded sx={{ color: 'success.main', fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="success.main">
                    Formulario Completado por el Postulante
                  </Typography>
                  {detail.applicantCompletedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Enviado el{' '}
                      {formatDate(detail.applicantCompletedAt, {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                endIcon={<OpenInNewRounded sx={{ fontSize: '14px !important' }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/admissions/${id}/application`, '_blank');
                }}
                sx={{
                  flexShrink: 0,
                  borderColor: 'rgba(16,185,129,0.4)',
                  color: 'success.main',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'success.main',
                    bgcolor: 'rgba(16,185,129,0.08)',
                  },
                }}
              >
                Ver Postulación
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      {/* ── Documents review ── */}
      {isAdmin &&
        ((detail.documentRequirements?.length ?? 0) > 0 ||
          (detail.reviewableDocuments?.length ?? 0) > 0) && (
          <Box mb={4}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <AdmissionDocumentsReview
                requirements={detail.documentRequirements ?? []}
                documents={detail.reviewableDocuments ?? []}
                busy={docBusy}
                onVerify={verifyDoc}
                onReject={rejectDoc}
              />
            </Paper>
          </Box>
        )}

      {/* Missing-documents override on accept */}
      {docOverride && (
        <Box mb={4}>
          <Alert
            severity="warning"
            action={
              <Button
                color="warning"
                size="small"
                onClick={() => handleAction('accept', docOverride.notes, true)}
              >
                Aceptar de todos modos
              </Button>
            }
          >
            {docOverride.message}
          </Alert>
        </Box>
      )}

      {/* The committee approved, but a rule blocked the automatic acceptance: the
          admission is the registrar's to accept (with an exception if it fits). */}
      {['under_review', 'waitlisted'].includes(detail.status) && detail.committeeApproval && (
        <Box mb={4}>
          <Alert severity="warning">
            {t('admissionDetail.committeeApproval.pending', {
              reason: tCommon(`errors.${detail.committeeApproval.blockedReason}`, {
                ...(detail.committeeApproval.blockedParams ?? {}),
                defaultValue: detail.committeeApproval.blockedReason,
              }),
            })}
          </Alert>
        </Box>
      )}

      {stackOverride && (
        <Box mb={4}>
          <Alert severity="warning">
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {stackOverride.message}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('admissionDetail.stackEntry.overrideHelp')}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'center' }}
            >
              <TextField
                size="small"
                fullWidth
                label={t('admissionDetail.stackEntry.reasonLabel')}
                value={stackOverride.reason}
                onChange={(e) => setStackOverride({ ...stackOverride, reason: e.target.value })}
              />
              <Button
                color="warning"
                variant="contained"
                size="small"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                disabled={!stackOverride.reason.trim() || actionLoading}
                onClick={() =>
                  handleAction(
                    'accept',
                    stackOverride.notes,
                    stackOverride.overrideDocuments,
                    stackOverride.reason.trim(),
                  )
                }
              >
                {t('admissionDetail.stackEntry.acceptAnyway')}
              </Button>
            </Stack>
          </Alert>
        </Box>
      )}

      {stackWarning && (
        <Box mb={4}>
          <Alert severity="info" onClose={() => setStackWarning(null)}>
            {stackWarning}
          </Alert>
        </Box>
      )}

      {/* ── Action Panel ── */}
      <Box mb={4}>
        <ActionPanel
          status={effectiveStatus}
          admissionId={id ?? ''}
          onAction={handleAction}
          actionLoading={actionLoading}
          actionError={actionError}
          isAdmin={isAdmin}
        />
      </Box>

      {/* ── Committee Review Panel (visible during and after review) ── */}
      {['under_review', 'accepted', 'rejected', 'waitlisted'].includes(effectiveStatus) && (
        <Box mb={4}>
          <ReviewPanel admissionId={id ?? ''} currentReviewerId={currentPersonId} />
        </Box>
      )}

      {/* ── Decision Notes ── */}
      {detail.decisionNotes && (
        <Alert
          severity={
            detail.status === 'accepted'
              ? 'success'
              : detail.status === 'waitlisted'
                ? 'warning'
                : 'error'
          }
          sx={{ mb: 4, borderRadius: 2 }}
          icon={
            detail.status === 'accepted' ? (
              <ThumbUpAltRounded />
            ) : detail.status === 'waitlisted' ? (
              <HourglassTopRounded />
            ) : (
              <ThumbDownAltRounded />
            )
          }
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Notas de decisión
          </Typography>
          <Typography variant="body2">{detail.decisionNotes}</Typography>
          {detail.decidedByName && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              — {detail.decidedByName}
              {detail.decidedAt
                ? `, ${formatDate(detail.decidedAt, { day: 'numeric', month: 'long', year: 'numeric' })}`
                : ''}
            </Typography>
          )}
        </Alert>
      )}

      {/* ── Info Cards ── */}
      <Typography variant="overline" color="text.disabled" sx={{ letterSpacing: 1 }}>
        Información del Postulante
      </Typography>
      <Divider sx={{ mb: 2, mt: 0.5 }} />
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={6}>
          <InfoCard
            icon={<PersonRounded fontSize="small" />}
            label="Persona"
            value={detail.personName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InfoCard
            icon={<SchoolRounded fontSize="small" />}
            label="Programa"
            value={detail.programName}
          />
        </Grid>
        {detail.personEmail && (
          <Grid item xs={12} sm={6}>
            <InfoCard
              icon={<PersonRounded fontSize="small" />}
              label="Email"
              value={detail.personEmail}
            />
          </Grid>
        )}
        {detail.personPhone && (
          <Grid item xs={12} sm={6}>
            <InfoCard
              icon={<PersonRounded fontSize="small" />}
              label="Teléfono"
              value={detail.personPhone}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <InfoCard
            icon={<CalendarMonthRounded fontSize="small" />}
            label="Período"
            value={detail.termName}
          />
        </Grid>
      </Grid>

      {/* ── Timeline ── */}
      <Typography variant="overline" color="text.disabled" sx={{ letterSpacing: 1 }}>
        Historial
      </Typography>
      <Divider sx={{ mb: 2, mt: 0.5 }} />
      <Stack gap={1.5}>
        {historyEvents.length === 0 && (
          <Typography variant="body2" color="text.disabled">
            Sin eventos registrados
          </Typography>
        )}
        {historyEvents.map((evt, idx) => (
          <Stack key={evt.id} direction="row" gap={2} alignItems="center">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: idx === historyEvents.length - 1 ? 'primary.main' : 'text.disabled',
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {actionLabel(evt.action)}
              {' — '}
              <strong>
                {formatDate(evt.occurredAt, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}{' '}
                {formatTime(evt.occurredAt, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </strong>
            </Typography>
          </Stack>
        ))}
      </Stack>

      {/* ── Withdraw Student Dialog ── */}
      {detail && currentTenantId && (
        <WithdrawStudentDialog
          open={withdrawOpen}
          onClose={() => setWithdrawOpen(false)}
          onSuccess={() => setWithdrawOpen(false)}
          tenantId={currentTenantId}
          admissionId={id ?? ''}
          studentName={detail.personName}
        />
      )}
    </Box>
  );
}
